import PropTypes from "prop-types"
import classnames from "classnames"
import "./style.scss"
import { map } from "lodash"
import { CardContent, Paper } from "@mui/material"
import { DATE } from "helpers/shapes"
import { FACTOR_TAB_OPTION_SHAPE } from "helpers/compositeSecurities"
import { computeCSVData } from "helpers/csv"
import { useTrack } from "helpers/analytics"
import { smallPercentageFormatter, percentageFormatter } from "helpers/formatters/numbers"
import NoData from "components/no-data"
import NotEnoughData from "components/not-enough-data"
import Tooltip from "components/analyze-tooltip"
import ExcelDownload from "components/excel-download"
import GraphTopBar from "components/graph-top-bar"
import MultiLine from "components/charts/multi-line"
import PerformanceDetails from "modules/analyze-composite-performance-details"

function AnalyzeCompositePerformance({
  account,
  modelId,
  analyzeViewSelectList,
  positionSetId,
  positionSetType,
  positionSetName,
  base,
  baseSignature,
  classifications,
  earliestDate,
  latestDate,
  selectedDate,
  startDate,
  performanceFetchStartDate,
  performanceHistory,
  endDate,
  setDate,
  data,
  segment,
  allCategoryItems,
  items,
  currentGrouping,
  expand,
  factorCategoryId,
  selectFactorCategory,
  options,
  focusChartLine,
  blurChartLine,
  focusedChartLine,
  viewType,
  fileName,
  selectedOption,
  toggle,
  toggleOnly,
  only,
  filtersVisible,
  isOpPro,
  selectedViews,
  displayAddView,
  addSelectedView,
  removeSelectedView,
  openDialog,
  availableSelectViewFactors,
  mustAggregateFactors,
  showNotHeld,
  setGrouping,
  factorsClassification,
}) {
  const trackExcelDownload = useTrack({
    type: "composite-analyze-performance-graph-timeseries-download",
  })

  let content

  if (!earliestDate || !latestDate) {
    content = <NoData title="Performance Trends" className="graph" />
  } else if (positionSetType === "portfolio" && (!data || data.length === 0)) {
    content = <NotEnoughData portfolio={positionSetId} account={account} isOpPro={isOpPro} />
  } else {
    content = <CardContent className={classnames("card-content")}>
      <Paper className="content">
        <div className="graph-container">
          <div className="graph-top">
            {analyzeViewSelectList}
            <GraphTopBar {...{
              displayAddView,
              openDialog,
              factors: availableSelectViewFactors,
              addView: ({ id }) => addSelectedView(id),
              value: factorCategoryId,
              onTabChanged: (_, factorId) => selectFactorCategory(factorId),
              tabs: map(options, ({ id, label, value, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: (factorId) => removeSelectedView(factorId, options),
                label: <><div>{label}</div><div>{percentageFormatter(value)}</div></>,
              })),
              actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackExcelDownload} />],
            }}
            />
          </div>
          <div className="graph">
            <MultiLine
              tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={(v) => v === "--" ? v : smallPercentageFormatter(v)} />}
              {...{
                tickFormat: smallPercentageFormatter,
                startDate,
                date: selectedDate,
                setDate,
                endDate,
                data,
                onlyItem: only,
                items,
                toggle,
                focusChartLine,
                blurChartLine,
                focusedChartLine,
                toggleOnly: (factorId) => toggleOnly(factorId, only !== factorId, items),
              }}
            />
          </div>
        </div>
        <div className="contributors">
          <PerformanceDetails {...{
            modelId,
            positionSetId,
            positionSetType,
            positionSetName,
            segment,
            mustAggregateFactors,
            allCategoryItems,
            classifications,
            currentGrouping,
            setGrouping,
            viewType,
            displayType: factorCategoryId,
            selectedDate,
            performanceFetchStartDate,
            performanceHistory,
            selectedOption,
            selectedViews,
            filtersVisible,
            showNotHeld,
            base,
            baseSignature,
            expand,
            factorsClassification,
          }}
          />
        </div>
      </Paper>
    </CardContent>
  }

  return (
    <div className="modules_analyze-composite-performance">
      {content}
    </div>
  )
}

AnalyzeCompositePerformance.propTypes = {
  analyzeViewSelectList: PropTypes.element,
  account: PropTypes.string.isRequired,
  positionSetId: PropTypes.string.isRequired,
  positionSetType: PropTypes.string.isRequired,
  positionSetName: PropTypes.string.isRequired,
  earliestDate: DATE,
  latestDate: DATE,
  startDate: DATE,
  performanceFetchStartDate: DATE.isRequired,
  performanceHistory: PropTypes.array.isRequired,
  endDate: DATE,
  firstDate: DATE,
  selectedDate: DATE,
  data: PropTypes.array.isRequired,
  segment: PropTypes.object,
  expand: PropTypes.bool.isRequired,
  mustAggregateFactors: PropTypes.bool.isRequired,
  favoriteFactors: PropTypes.object,
  factorsMetadata: PropTypes.object,
  allCategoryItems: PropTypes.array.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  currentGrouping: PropTypes.object,
  classifications: PropTypes.array,
  categories: PropTypes.array,
  factorCategoryId: PropTypes.string.isRequired,
  selectFactorCategory: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  setDate: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  focusedChartLine: PropTypes.string,
  options: PropTypes.arrayOf(FACTOR_TAB_OPTION_SHAPE).isRequired,
  fileName: PropTypes.string.isRequired,
  selectedOption: PropTypes.object.isRequired,

  only: PropTypes.string,
  toggle: PropTypes.func.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  disabled: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  filtersVisible: PropTypes.bool.isRequired,
  isOpPro: PropTypes.bool.isRequired,
  selectedViews: PropTypes.array,
  selectedViewsFactors: PropTypes.array,
  displayAddView: PropTypes.bool.isRequired,
  addSelectedView: PropTypes.func.isRequired,
  removeSelectedView: PropTypes.func.isRequired,
  modelId: PropTypes.string.isRequired,
  openDialog: PropTypes.func.isRequired,
  availableSelectViewFactors: PropTypes.array.isRequired,
  showNotHeld: PropTypes.bool.isRequired,
  base: PropTypes.object,
  baseSignature: PropTypes.string,
  setGrouping: PropTypes.func.isRequired,
  factorsClassification: PropTypes.object,
}

export default AnalyzeCompositePerformance
